import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Download, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import QRCode from "react-qr-code";

const Admin2FASetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [step, setStep] = useState<'generate' | 'verify' | 'backup'>('generate');
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('setup-2fa', {
        body: { action: 'generate' }
      });

      if (error) throw error;

      setQrCodeUrl(data.qrCodeUrl);
      setSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setStep('verify');
    } catch (error: any) {
      console.error('Erro ao gerar 2FA:', error);
      toast({
        title: "Erro ao gerar código",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Código inválido",
        description: "Digite um código de 6 dígitos",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);

    try {
      const { data, error } = await supabase.functions.invoke('setup-2fa', {
        body: { 
          action: 'verify',
          verificationCode 
        }
      });

      if (error) throw error;

      if (data.success) {
        setStep('backup');
        toast({
          title: "2FA configurado!",
          description: "Salve seus códigos de backup",
        });
      } else {
        toast({
          title: "Código incorreto",
          description: "Verifique o código no Google Authenticator",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Erro ao verificar setup:', error);
      toast({
        title: "Erro na verificação",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast({
      title: "Copiado!",
      description: "Código secreto copiado para área de transferência",
    });
  };

  const downloadBackupCodes = () => {
    const text = `Justiçai - Códigos de Backup 2FA\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes-2fa.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100" />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="backdrop-blur-xl bg-white/70 rounded-2xl shadow-2xl border border-white/20 p-8 space-y-6">
            
            {step === 'generate' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-4 text-gray-600">Gerando configuração...</p>
              </div>
            )}

            {step === 'verify' && (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-blue-500/10 rounded-full">
                    <Shield className="h-12 w-12 text-blue-600" />
                  </div>
                </div>

                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Configure o Google Authenticator
                  </h1>
                  <p className="text-sm text-gray-600">
                    Escaneie o QR Code ou digite o código manualmente
                  </p>
                </div>

                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <QRCode value={qrCodeUrl} size={200} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Ou digite este código manualmente:
                  </label>
                  <div className="flex gap-2">
                    <Input 
                      value={secret} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={copySecret}
                    >
                      {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Digite o código do app para confirmar:
                    </label>
                    <Input
                      type="text"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-2xl tracking-widest font-mono h-14"
                      maxLength={6}
                      onKeyPress={(e) => e.key === 'Enter' && handleVerifySetup()}
                    />
                  </div>

                  <Button 
                    onClick={handleVerifySetup}
                    disabled={isVerifying || verificationCode.length !== 6}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                  >
                    {isVerifying ? 'Verificando...' : 'Ativar 2FA'}
                  </Button>
                </div>
              </>
            )}

            {step === 'backup' && (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-green-500/10 rounded-full">
                    <Check className="h-12 w-12 text-green-600" />
                  </div>
                </div>

                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-semibold text-gray-900">
                    2FA Ativado!
                  </h1>
                  <p className="text-sm text-gray-600">
                    Salve estes códigos de backup em um lugar seguro
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="font-mono text-sm text-gray-700 bg-white p-2 rounded text-center">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={downloadBackupCodes}
                  variant="outline"
                  className="w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Baixar Códigos
                </Button>

                <Button 
                  onClick={() => navigate('/admin-2fa-verify')}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                >
                  Continuar para o Painel
                </Button>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin2FASetup;
